import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { switchMap, map, catchError, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { SessaoService } from '../sessao/sessao.service';

import { environment } from '../../environments/environment';
import { GoogleAuthService } from './google-auth.service';
import { AppleAuthService } from './apple-auth.service';
import { AuthService } from '../auth.service';
import { SafeStorageService } from '../../shared/safe-storage.service';
import { UsuarioService } from '../usuario/usuario.service';
import {
  GoogleAuthVerifyDTO,
  GoogleRegisterDTO,
  AppleRegisterDTO,
  AppleLoginDTO,
  PrimeiroCadastroSocialDTO,
  SocialUserData,
} from '../../models/auth/social-login.model';
import { UsuarioContextoDTO } from '../../models/usuario.model';

/**
 * Service orquestrador para login/cadastro social (Google + Apple).
 *
 * NOTA: Usa HttpClient diretamente (em vez de ApiService) para os endpoints
 * sociais porque precisamos acessar headers da resposta (Authorization)
 * via observe: 'response'. O ApiService.create() nao retorna headers.
 * Conforme aprovado no R_APPROVED_FE (observacao 3).
 */
@Injectable({ providedIn: 'root' })
export class SocialLoginService {

  private baseUrl = environment.apiBaseUrl;

  constructor(
    private http: HttpClient,
    private googleAuthService: GoogleAuthService,
    private appleAuthService: AppleAuthService,
    private authService: AuthService,
    private safe: SafeStorageService,
    private usuarioService: UsuarioService,
    private router: Router,
    private sessaoService: SessaoService,
  ) {}

  /**
   * Fluxo Google completo (chamado pelo LoginComponent):
   * 1. GoogleAuthService.signIn() -> { idToken, email, nome }
   * 2. POST /google/auth/login { idToken } (endpoint exclusivo da web, retorna JWT)
   * 3a. Se 200 -> usuario existe -> completar login
   * 3b. Se 403 -> usuario novo -> retorna dados para signup
   */
  loginComGoogle(): Observable<{ tipo: 'login_ok' } | { tipo: 'novo_usuario'; dados: SocialUserData }> {
    return this.googleAuthService.signIn().pipe(
      switchMap((googleData) => {
        const body: GoogleAuthVerifyDTO = {
          idToken: googleData.idToken,
          // Sessao unica por plataforma: identifica a vaga (WEB) e este navegador.
          plataforma: this.sessaoService.plataforma,
          deviceId: this.sessaoService.obterDeviceId(),
        };

        // Tenta login via /google/auth/login (retorna JWT no header Authorization)
        return this.http.post<any>(
          `${this.baseUrl}/google/auth/login`,
          body,
          {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            observe: 'response',
          }
        ).pipe(
          switchMap((response) => {
            // Status 200 - Usuario existe, completar login com JWT
            return this.completarLogin(response).pipe(
              map(() => ({ tipo: 'login_ok' as const }))
            );
          }),
          catchError((err) => {
            if (err.status === 403) {
              // Status 403 - Usuario novo, redirecionar para cadastro
              const dados: SocialUserData = {
                email: googleData.email,
                nome: googleData.nome,
                provedor: 'google',
                token: googleData.idToken,
              };
              return of({ tipo: 'novo_usuario' as const, dados });
            }
            return throwError(() => err);
          })
        );
      })
    );
  }

  /**
   * Fluxo Apple completo (chamado pelo LoginComponent):
   * 1. AppleAuthService.signIn() -> { identityToken, authorizationCode, email, nome }
   * 2. Tenta POST /apple/firebase/login (sempre, tanto com email null quanto disponivel)
   * 3. Se 200 -> login completo
   * 4. Se 404/403 e tem email -> usuario novo -> retorna dados para signup
   * 5. Se 404/403 e sem email -> erro (conta nao encontrada)
   */
  loginComApple(): Observable<{ tipo: 'login_ok' } | { tipo: 'novo_usuario'; dados: SocialUserData }> {
    return this.appleAuthService.signIn().pipe(
      switchMap((appleData) => {
        const loginBody: AppleLoginDTO = {
          idToken: appleData.identityToken,
          fallbackEmail: appleData.email ?? undefined,
          // Sessao unica por plataforma: identifica a vaga (WEB) e este navegador.
          plataforma: this.sessaoService.plataforma,
          deviceId: this.sessaoService.obterDeviceId(),
        };

        return this.http.post<any>(
          `${this.baseUrl}/apple/firebase/login`,
          loginBody,
          {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            observe: 'response',
          }
        ).pipe(
          switchMap((response) => {
            // Login bem-sucedido
            return this.completarLogin(response).pipe(
              map(() => ({ tipo: 'login_ok' as const }))
            );
          }),
          catchError((err) => {
            if (err.status === 403 || err.status === 404) {
              // Usuario novo
              if (appleData.email) {
                // Tem email - pode cadastrar
                const dados: SocialUserData = {
                  email: appleData.email,
                  nome: appleData.nome,
                  provedor: 'apple',
                  token: appleData.identityToken,
                  authorizationCode: appleData.authorizationCode,
                };
                return of({ tipo: 'novo_usuario' as const, dados });
              }
              // Sem email (email privado) e nao cadastrado - erro
              return throwError(() => new Error('APPLE_NO_EMAIL_NOT_REGISTERED'));
            }
            return throwError(() => err);
          })
        );
      })
    );
  }

  /**
   * Registro Google (chamado pelo SignupComponent em modo google):
   * 1. Monta GoogleRegisterDTO
   * 2. POST /google/register (cria usuario)
   * 3. POST /google/auth/login (obtem JWT do backend)
   * 4. Completa login salvando token
   */
  registrarComGoogle(token: string, formData: PrimeiroCadastroSocialDTO): Observable<void> {
    const body: GoogleRegisterDTO = {
      googleAuthRequest: { idToken: token },
      primeiroCadastroDTO: formData,
    };

    return this.http.post<any>(
      `${this.baseUrl}/google/register`,
      body,
      {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        observe: 'response',
      }
    ).pipe(
      // Apos registro, fazer login via /google/auth/login para obter token JWT
      switchMap(() => {
        const loginBody: GoogleAuthVerifyDTO = { idToken: token };
        return this.http.post<any>(
          `${this.baseUrl}/google/auth/login`,
          loginBody,
          {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
            observe: 'response',
          }
        );
      }),
      switchMap((loginResponse) => this.completarLogin(loginResponse))
    );
  }

  /**
   * Registro Apple (chamado pelo SignupComponent em modo apple):
   * 1. Monta AppleRegisterDTO
   * 2. POST /apple/firebase/register
   * 3a. Se sucesso -> completa login
   * 3b. Se erro "ja cadastrado" -> fallback POST /apple/firebase/login
   */
  registrarComApple(socialData: SocialUserData, formData: PrimeiroCadastroSocialDTO): Observable<void> {
    const body: AppleRegisterDTO = {
      idToken: socialData.token,
      fallbackEmail: formData.emailUsuario || null,
      primeiroCadastroDTO: formData,
    };

    return this.http.post<any>(
      `${this.baseUrl}/apple/firebase/register`,
      body,
      {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        observe: 'response',
      }
    ).pipe(
      switchMap((response) => this.completarLogin(response)),
      catchError((err) => {
        // Se ja cadastrado, tentar login direto
        const mensagem = err.error?.message || '';
        if (mensagem.toLowerCase().includes('cadastrad') || err.status === 409) {
          return this.fallbackAppleLogin(socialData);
        }
        return throwError(() => err);
      })
    );
  }

  /**
   * Fallback: tenta login Apple quando registro retorna "ja cadastrado"
   */
  private fallbackAppleLogin(socialData: SocialUserData): Observable<void> {
    const loginBody: AppleLoginDTO = {
      idToken: socialData.token,
      fallbackEmail: socialData.email ?? undefined,
      // Sessao unica por plataforma: identifica a vaga (WEB) e este navegador.
      plataforma: this.sessaoService.plataforma,
      deviceId: this.sessaoService.obterDeviceId(),
    };

    return this.http.post<any>(
      `${this.baseUrl}/apple/firebase/login`,
      loginBody,
      {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        observe: 'response',
      }
    ).pipe(
      switchMap((response) => this.completarLogin(response))
    );
  }

  /**
   * Fluxo pos-login compartilhado:
   * 1. Extrai token JWT do header Authorization da response
   * 2. Salva token via SafeStorageService
   * 3. Extrai userId do body
   * 4. GET /usuario/{userId} para obter empresaId
   * 5. Salva empresaId via SafeStorageService
   * 6. AuthService.setAuthenticated(true)
   */
  private completarLogin(response: any): Observable<void> {
    // Extrair token do header Authorization
    const authHeader = response?.headers?.get?.('Authorization');
    if (authHeader) {
      this.safe.setItem('authToken', authHeader);
    }

    // Extrair token do body (alguns endpoints retornam no body)
    const bodyToken = response?.body?.token || response?.body?.accessToken;
    if (bodyToken && !authHeader) {
      this.safe.setItem('authToken', bodyToken.startsWith('Bearer ') ? bodyToken : `Bearer ${bodyToken}`);
    }

    const userId = response?.body?.id;
    if (userId !== undefined && userId !== null) {
      this.safe.setItem('userId', String(userId));
    }

    if (!userId && userId !== 0) {
      // Se nao tem userId no body, tenta autenticar mesmo assim
      this.authService.setAuthenticated(true);
      return of(void 0);
    }

    // Buscar dados do usuario para obter empresaId, email e categoriaAdmin
    return this.usuarioService.getContexto(`/usuario/${userId}`).pipe(
      tap((usuario: UsuarioContextoDTO) => {
        const empresaId = usuario?.empresaId;
        if (empresaId != null) {
          this.safe.setItem('id', String(empresaId));
        }
        // Salvar email, categoriaAdmin e categoriaAcessoMaster para uso do PermissaoService
        if (usuario?.email) {
          this.safe.setItem('email', usuario.email);
        }
        if (usuario?.categoriaAdmin != null) {
          this.safe.setItem('categoriaAdmin', String(usuario.categoriaAdmin));
        }
        if (usuario?.categoriaAcessoMaster != null) {
          this.safe.setItem('categoriaAcessoMaster', String(usuario.categoriaAcessoMaster));
        }
        if (usuario?.contador != null) {
          this.safe.setItem('contador', String(usuario.contador));
        }
        this.authService.setAuthenticated(true);
      }),
      map(() => void 0)
    );
  }
}
